/** Central runtime authority for browser HTTP, resource, and WebSocket URLs. */

interface RuntimeLocation {
    protocol: string;
    host: string;
}

const API_V1_PATH = '/api/v1';
const WS_ZIPPY_PATH = '/ws/zippy';

const getRuntimeLocation = (): RuntimeLocation | undefined =>
    typeof window !== 'undefined' ? window.location : undefined;

const trimTrailingSlash = (value: string): string => value.replace(/\/+$/, '');

const configuredOverride = (name: 'api' | 'websocket' | 'xtsCredentials'): string => {
    const value = {
        api: process.env.REACT_APP_API_BASE_URL,
        websocket: process.env.REACT_APP_WS_URL,
        xtsCredentials: process.env.REACT_APP_XTS_CREDENTIALS_URL,
    }[name];
    return value?.trim() || '';
};

/**
 * HTTPS deployments always use the reverse proxy on the current origin.
 * Explicit overrides are reserved for HTTP local development.
 */
export const resolveApiBaseUrl = (location: RuntimeLocation | undefined = getRuntimeLocation()): string => {
    if (location?.protocol !== 'https:') {
        const override = configuredOverride('api');
        if (override) {
            return trimTrailingSlash(override);
        }
    }
    return API_V1_PATH;
};

export const API_V1_BASE_URL = resolveApiBaseUrl();

export const resolveApiUrl = (path: string): string =>
    `${API_V1_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;

/** Resolve a backend root path, respecting the local API override's origin. */
export const resolveBackendUrl = (path: string): string => {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    if (/^https?:\/\//i.test(API_V1_BASE_URL)) {
        return new URL(normalizedPath, API_V1_BASE_URL).toString();
    }
    return normalizedPath;
};

/** Route legacy and new comment image values through the proxied API. */
export const resolveUploadUrl = (value: string): string => {
    if (!value) {
        return value;
    }

    let path = value;
    try {
        path = new URL(value).pathname;
    } catch {
        // Relative values are expected from the backend.
    }

    if (path.startsWith('/uploads/')) {
        return resolveApiUrl(path);
    }
    if (path.startsWith(`${API_V1_PATH}/uploads/`)) {
        return resolveBackendUrl(path);
    }
    return value;
};

export const resolveWebSocketUrl = (location: RuntimeLocation | undefined = getRuntimeLocation()): string => {
    if (location?.protocol !== 'https:') {
        const override = configuredOverride('websocket');
        if (override) {
            return trimTrailingSlash(override);
        }
    }
    if (!location?.host) {
        throw new Error('WebSocket URL requires REACT_APP_WS_URL or a browser location');
    }
    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${location.host}${WS_ZIPPY_PATH}`;
};

export const WS_ZIPPY_URL = resolveWebSocketUrl();

export const APP_HOST = getRuntimeLocation()?.host || 'Zippy';

export const TOKEN_SERVICE_URL = resolveApiUrl('/token-service');
export const HEALTH_CHECK_URL = resolveBackendUrl('/health');
export const ORDERS_URL = resolveApiUrl('/orders');
export const POSITIONS_URL = resolveApiUrl('/positions');
export const BROKERS_URL = resolveApiUrl('/brokers');
export const MTM_URL = resolveApiUrl('/mtm');
export const COMMENTS_URL = resolveApiUrl('/comments');

export const resolveXtsCredentialsUrl = (
    location: RuntimeLocation | undefined = getRuntimeLocation(),
): string => {
    if (location?.protocol !== 'https:') {
        const override = configuredOverride('xtsCredentials');
        if (override) {
            return trimTrailingSlash(override);
        }
    }
    return resolveApiUrl('/xts-credentials');
};

export const XTS_CREDENTIALS_URL = resolveXtsCredentialsUrl();

export const getConfigInfo = () => ({
    appHost: APP_HOST,
    apiV1BaseUrl: API_V1_BASE_URL,
    websocketUrl: WS_ZIPPY_URL,
    environment: process.env.NODE_ENV || 'development',
});

export const logConfigInfo = () => {
    console.log('🔧 API Configuration:', {
        ...getConfigInfo(),
        timestamp: new Date().toISOString(),
    });
};
